import React, { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../overmind';
import { useNavigate, useSearchParams } from 'react-router';
import { KeplerState } from '../../../models/kepler-state';
import { AuthenticationStatus as STATUS } from '../../../enums/authentication-status';
import { SSOButton } from '../sso-button/sso-button';
import { AccountPageLoaderLayer } from '../../../components/general/loading-state/loader-layers/account-page-loader-layer/account-page-loader-layer';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { Button, EmailValidator, FieldController, RequiredValidator, TextField, useMatchScreenWidth } from '@keplerco/core';
import accountStyles from '../account.module.css';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import classNames from 'classnames';
import { TertiaryButton } from '../../../components/general/tertiary-button/tertiary-button';
import { LoginInput } from '../../../models';
import { RegistrationType } from '../../../enums/registration-type';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const actions = useAppActions();
  const { authenticationStatus, samlUrl, registrationType } = useAppState<KeplerState>();
  const { control, handleSubmit } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });
  const isMobile = useMatchScreenWidth('mobile');
  const [removeErrorMessage, setRemoveErrorMessage] = useState<boolean>(true);

  return (
    <div className={accountStyles.accountBackground} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className={accountStyles.accountPage}>
        <div className={classNames('card', accountStyles.accountCard)} style={{ overflow: 'hidden' }}>
          <AccountPageLoaderLayer path={PagePath.accountLogin}>
            {!isMobile && (
              <div className={accountStyles.accountNavigationToggler}>
                {registrationType !== RegistrationType.NotAllowed ? (
                  <Fragment>
                    <span className="body">Not registered?</span>
                    {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                    <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountRegister}`)}>
                      Create an account
                    </TertiaryButton>
                  </Fragment>
                ) : (
                  <Fragment>
                    <span className="body">Not registered?</span>
                    {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                    <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountActivate}`)}>
                      Activate your account
                    </TertiaryButton>
                  </Fragment>
                )}
              </div>
            )}

            <h1>Sign In</h1>

            <div style={{ marginTop: 12 }}>Fill in your company email address and your password</div>

            <form
              id="login"
              className={accountStyles.accountForm}
              autoComplete="off"
              onSubmit={handleSubmit(async fieldValues => {
                actions.startLoader({ path: PagePath.accountLogin, type: FetchType.PageFetching });
                window.scrollTo(0, 0);
                setRemoveErrorMessage(false);
                await actions.login(fieldValues as LoginInput);
                actions.stopLoader(PagePath.accountLogin);
              })}
            >
              {authenticationStatus === STATUS.AuthenticationFailed && !removeErrorMessage && <div className="formErrorMessage">Incorrect password or email address</div>}

              <FieldController
                control={control}
                name="email"
                rules={new EmailValidator()}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="Email"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your email',
                    }}
                  />
                )}
              />

              <FieldController
                control={control}
                name="password"
                rules={new RequiredValidator('Enter your password')}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Password"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your password',
                    }}
                  />
                )}
              />

              {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
              <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountForgotPassword}`)} style={{ marginLeft: 'auto' }}>
                Forgot password?
              </TertiaryButton>

              <Button fullWidthMobile={true} type="button">
                Sign In
              </Button>
            </form>

            <div className={accountStyles.accountDivider}>Or sign in using</div>

            {!!samlUrl && (
              <SSOButton
                ssoType="saml"
                onClick={async () => {
                  window.location.href = !!returnTo ? `${samlUrl}?returnUrl=${returnTo}` : samlUrl;
                }}
              />
            )}

            <SSOButton
              ssoType="google"
              onClick={async () => {
                actions.startLoader({ path: PagePath.accountLogin, type: FetchType.PageFetching });
                await actions.externalLogin({ provider: 'Google' });
                actions.stopLoader(PagePath.accountLogin);
              }}
            />

            <SSOButton
              ssoType="microsoft"
              onClick={async () => {
                actions.startLoader({ path: PagePath.accountLogin, type: FetchType.PageFetching });
                await actions.externalLogin({ provider: 'Microsoft' });
                actions.stopLoader(PagePath.accountLogin);
              }}
            />

            <SSOButton
              ssoType="linkedin"
              onClick={async () => {
                actions.startLoader({ path: PagePath.accountLogin, type: FetchType.PageFetching });
                await actions.externalLogin({ provider: 'LinkedIn' });
                actions.stopLoader(PagePath.accountLogin);
              }}
            />

            {isMobile && (
              <div className={classNames(accountStyles.accountNavigationToggler, accountStyles.bottom)}>
                {registrationType !== RegistrationType.NotAllowed ? (
                  <Fragment>
                    <span className="body">Not registered?</span>
                    {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                    <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountRegister}`)}>
                      Create an account
                    </TertiaryButton>
                  </Fragment>
                ) : (
                  <Fragment>
                    <span className="body">Not registered?</span>
                    {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                    <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountActivate}`)}>
                      Activate your account
                    </TertiaryButton>
                  </Fragment>
                )}
              </div>
            )}
          </AccountPageLoaderLayer>
        </div>
      </div >
    </div >
  );
}
